import { useApolloClient } from '@apollo/react-hooks'
import PortalLearningPaths from '@classes/LearningPaths'
import PortalStudentProfile from '@classes/StudentProfile'
import { StoreProps } from '@interfaces/StoreState'
import { RegistrationMaterial } from '@interfaces/Student'
import { GET_LEARNING_PATH_BY_LPIDS } from '@queries/learningPath'
import { MY_BERLITZ_ENV, PROFILE_TYPE, PROGRAM_TYPES } from '@utils/constants'
import { decodeDDUniqueKey } from '@utils/helpers'
import {
  updateDashboardActiveMaterial,
  updateDashboardActiveProgram,
  updateDashboardActiveRegistration,
} from '@redux/actions/dashboard'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { hasPublishedScormUnits, isScormAllowed } from '../helpers'

export interface UseProfessionalDevelopmentDataResult {
  loading: boolean
  LearningPaths: PortalLearningPaths
  canShowPdTab: boolean
  error?: Error
}

export const useProfessionalDevelopmentData = (): UseProfessionalDevelopmentDataResult => {
  const client = useApolloClient()
  const dispatch = useDispatch()

  const { profileInfo, selectedLP, activeRegistration, activeProgram, userPermissions } = useSelector(
    ({ userProfile, dashboard, permissions }: StoreProps) => ({
      profileInfo: userProfile?.info?.StudentProfile?.StudentProfileInfo,
      selectedLP: dashboard.activeMaterial || undefined,
      activeRegistration: dashboard.activeRegistration || undefined,
      activeProgram: dashboard.activeProgram || undefined,
      userPermissions: permissions.permissions,
    }),
    shallowEqual
  )

  const studentProfile = useMemo(
    () => (profileInfo ? new PortalStudentProfile(profileInfo) : undefined),
    [profileInfo]
  )

  const LPModuleFlag = userPermissions['flag::LP-modules']?.show

  let activeLPID = selectedLP
  let selectedRegistrationId = activeRegistration
  let selectedProgramId = activeProgram

  if (selectedLP?.includes('|')) {
    const decoded = decodeDDUniqueKey(selectedLP)
    activeLPID = decoded?.materialLPID
    selectedRegistrationId = decoded?.registrationId
    selectedProgramId = decoded?.programId
  }

  const studentIdForQuery = useMemo(() => {
    let sfId = studentProfile?.SFId
    if (activeLPID && studentProfile) {
      let materialType: string | undefined
      if (selectedRegistrationId && selectedProgramId) {
        const regProgMaterials =
          studentProfile.RegistrationProgramMaterials?.[selectedRegistrationId]?.programs?.[selectedProgramId]?.materials
        if (regProgMaterials?.[activeLPID]) materialType = regProgMaterials[activeLPID].ProgramType
      }
      if (!materialType && studentProfile.Materials?.[activeLPID]) {
        materialType = studentProfile.Materials[activeLPID].ProgramType
      }
      if (materialType && PROGRAM_TYPES.F2F_STAR.includes(materialType) && studentProfile.SFId2B) {
        sfId = studentProfile.SFId2B
      }
    }
    return sfId
  }, [studentProfile, activeLPID, selectedRegistrationId, selectedProgramId])

  const lpIdsString = useMemo(() => {
    if (!studentProfile || !activeLPID) return ''
    let match = studentProfile.Materials?.[activeLPID]
    const additionalMaterials: RegistrationMaterial[] = []
    if (!LPModuleFlag) {
      if (match?.IsElective) {
        const core = studentProfile.Materials?.[match.FoundationMaterialId!]
        if (core) additionalMaterials.push(core)
      } else if (match?.ElectiveMaterials?.length) {
        for (const lpid in studentProfile.Materials) {
          if (Object.prototype.hasOwnProperty.call(studentProfile.Materials, lpid)) {
            const material = studentProfile.Materials[lpid]
            if (match?.ElectiveMaterials?.find((e) => e.LPID === lpid)) additionalMaterials.push(material)
          }
        }
      }
    }
    return [match, ...additionalMaterials].filter(Boolean).map((lp) => lp?.LPID).join('|')
  }, [studentProfile, activeLPID, LPModuleFlag])

  const [lpData, setLpData] = useState<any>(undefined)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | undefined>()
  const [reloadToken, setReloadToken] = useState(0)

  const fetchData = useCallback(async () => {
    if (!studentProfile?.SFId || !lpIdsString) {
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const result = await client.query({
        query: GET_LEARNING_PATH_BY_LPIDS,
        variables: {
          StudentId: studentIdForQuery,
          IdType: PROFILE_TYPE.STUDENT,
          Ids: lpIdsString,
          berlitzEnv: studentProfile?.MyBerlitzEnv ?? MY_BERLITZ_ENV.DEFAULT,
        },
        fetchPolicy: 'network-only',
      })
      setLpData(result.data?.getLearningPathByIds)
      setError(undefined)
    } catch (err) {
      setError(err as Error)
      setLpData(undefined)
    } finally {
      setLoading(false)
    }
  }, [client, studentProfile, studentIdForQuery, lpIdsString])

  useEffect(() => {
    fetchData()
  }, [fetchData, reloadToken])

  const sfId = studentProfile?.SFId
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isProfessionalTab = window.location.hash.includes(btoa('ProfessionalDevelopmentTab'))
      if (document.hidden || !sfId || !isProfessionalTab) return
      const reg = JSON.parse(localStorage.getItem('portal-selected-registration') || '{}')[sfId]
      const prog = JSON.parse(localStorage.getItem('portal-selected-program') || '{}')[sfId]
      const mat = JSON.parse(localStorage.getItem('portal-selected-material') || '{}')[sfId]
      dispatch(updateDashboardActiveRegistration(reg))
      dispatch(updateDashboardActiveProgram(prog))
      dispatch(updateDashboardActiveMaterial(mat))
      setReloadToken((t) => t + 1)
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [dispatch, sfId])

  const LearningPaths = useMemo(
    () => new PortalLearningPaths(loading ? [] : lpData, studentProfile, undefined),
    [lpData, loading, studentProfile]
  )

  const isScormMaterial = useMemo(() => {
    if (!studentProfile || !selectedRegistrationId || !activeLPID) return false
    const regInfo = studentProfile.getRegistrationById(selectedRegistrationId)?.RegistrationInfo
    return regInfo?.Materials?.find((m: any) => m.LPID === activeLPID)?.IsScormMaterial === true
  }, [studentProfile, selectedRegistrationId, activeLPID])

  const canShowPdTab =
    !!hasPublishedScormUnits(LearningPaths) &&
    isScormMaterial &&
    isScormAllowed(studentProfile?.student)

  return { loading, LearningPaths, canShowPdTab, error }
}
